import React, { useState } from "react";
import { motion } from "framer-motion";
import { Heart, Users, ShieldCheck, Handshake, Sparkles } from "lucide-react";
import { useLang } from "@/contexts/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { api } from "@/lib/api";

const valueIcons = [Heart, ShieldCheck, Users, Handshake];

const fadeUp = {
    initial: { opacity: 0, y: 24 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, margin: "-40px" },
    transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] },
};

export default function About() {
    const { t, lang } = useLang();
    const a = t.aboutPage;
    const [form, setForm] = useState({
        full_name: "",
        email: "",
        phone: "",
        country: "",
        member_type: "ordinary",
        motivation: "",
    });
    const [loading, setLoading] = useState(false);

    const onChange = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

    const onSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await api.submitMembership({ ...form, language: lang });
            toast.success(a.joinSuccess);
            setForm({ full_name: "", email: "", phone: "", country: "", member_type: "ordinary", motivation: "" });
        } catch (err) {
            toast.error(a.joinError);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div data-testid="page-about" className="px-6 md:px-10 py-12 md:py-20">
            <div className="max-w-7xl mx-auto">
                {/* Header */}
                <motion.div {...fadeUp} className="max-w-3xl">
                    <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-caireen-olive/15 text-caireen-olive font-sans text-xs tracking-widest uppercase">
                        <Sparkles className="w-3.5 h-3.5" />
                        Caireen Verein
                    </span>
                    <h1 className="mt-5 font-serif text-5xl md:text-6xl tracking-tight leading-[1.05]">
                        {a.title}
                    </h1>
                    <p className="mt-5 font-sans text-lg text-caireen-ink/70 max-w-2xl">{a.sub}</p>
                </motion.div>

                {/* Values */}
                <section className="mt-20" data-testid="about-values">
                    <motion.h2 {...fadeUp} className="font-serif text-3xl md:text-4xl tracking-tight">
                        {a.valuesTitle}
                    </motion.h2>
                    <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                        {a.values.map((v, i) => {
                            const Icon = valueIcons[i % valueIcons.length];
                            return (
                                <motion.div
                                    key={i}
                                    {...fadeUp}
                                    transition={{ ...fadeUp.transition, delay: i * 0.08 }}
                                    className="bg-white rounded-3xl p-7 ring-1 ring-caireen-ink/10 hover:shadow-[0_24px_60px_-24px_rgba(31,41,55,0.18)] transition-all"
                                >
                                    <Icon className="w-7 h-7 text-caireen-orange" strokeWidth={1.75} />
                                    <p className="mt-4 font-serif text-xl">{v.t}</p>
                                    <p className="mt-1.5 font-sans text-sm text-caireen-ink/65 leading-relaxed">{v.d}</p>
                                </motion.div>
                            );
                        })}
                    </div>
                </section>

                {/* Structure */}
                <section className="mt-24" data-testid="about-structure">
                    <motion.h2 {...fadeUp} className="font-serif text-3xl md:text-4xl tracking-tight">
                        {a.structureTitle}
                    </motion.h2>
                    <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-5">
                        {a.structure.map((s, i) => {
                            const accents = ["bg-caireen-olive", "bg-caireen-orange", "bg-caireen-blue", "bg-caireen-red"];
                            return (
                                <motion.div
                                    key={i}
                                    {...fadeUp}
                                    transition={{ ...fadeUp.transition, delay: i * 0.07 }}
                                    className="relative bg-white rounded-3xl p-7 ring-1 ring-caireen-ink/10 overflow-hidden"
                                >
                                    <span className={`absolute left-0 top-7 bottom-7 w-1 rounded-r-full ${accents[i % accents.length]}`} />
                                    <p className="font-serif text-2xl">{s.t}</p>
                                    <p className="mt-2 font-sans text-sm text-caireen-ink/70 leading-relaxed">{s.d}</p>
                                </motion.div>
                            );
                        })}
                    </div>
                </section>

                {/* Join form */}
                <section className="mt-24 grid grid-cols-1 lg:grid-cols-12 gap-10" data-testid="about-join">
                    <motion.div {...fadeUp} className="lg:col-span-5 space-y-5">
                        <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-caireen-orange/10 text-caireen-orange font-sans text-xs tracking-widest uppercase">
                            <Users className="w-3.5 h-3.5" />
                            {t.nav.join}
                        </span>
                        <h2 className="font-serif text-4xl md:text-5xl tracking-tight">{a.joinTitle}</h2>
                        <p className="font-sans text-base text-caireen-ink/70">{a.joinSub}</p>
                        <div className="relative aspect-[4/3] rounded-3xl overflow-hidden ring-1 ring-caireen-ink/10 mt-6">
                            <img
                                src="https://images.unsplash.com/photo-1521737604893-d14cc237f11d?auto=format&fit=crop&w=1000&q=80"
                                alt="Team"
                                className="w-full h-full object-cover"
                            />
                        </div>
                    </motion.div>

                    <motion.form
                        {...fadeUp}
                        onSubmit={onSubmit}
                        className="lg:col-span-7 bg-white rounded-3xl p-7 md:p-10 ring-1 ring-caireen-ink/10 space-y-5"
                        data-testid="membership-form"
                    >
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label className="font-sans text-sm">{a.fullName}</Label>
                                <Input data-testid="membership-name" required value={form.full_name} onChange={onChange("full_name")} className="rounded-xl bg-caireen-cream/50 border-caireen-ink/15 h-12" />
                            </div>
                            <div className="space-y-2">
                                <Label className="font-sans text-sm">Email</Label>
                                <Input data-testid="membership-email" required type="email" value={form.email} onChange={onChange("email")} className="rounded-xl bg-caireen-cream/50 border-caireen-ink/15 h-12" />
                            </div>
                            <div className="space-y-2">
                                <Label className="font-sans text-sm">{a.phone}</Label>
                                <Input data-testid="membership-phone" value={form.phone} onChange={onChange("phone")} className="rounded-xl bg-caireen-cream/50 border-caireen-ink/15 h-12" />
                            </div>
                            <div className="space-y-2">
                                <Label className="font-sans text-sm">{a.country}</Label>
                                <Input data-testid="membership-country" value={form.country} onChange={onChange("country")} className="rounded-xl bg-caireen-cream/50 border-caireen-ink/15 h-12" />
                            </div>
                        </div>
                        <div className="space-y-2">
                            <Label className="font-sans text-sm">{a.memberType}</Label>
                            <Select value={form.member_type} onValueChange={(v) => setForm((s) => ({ ...s, member_type: v }))}>
                                <SelectTrigger data-testid="membership-type" className="rounded-xl bg-caireen-cream/50 border-caireen-ink/15 h-12">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="ordinary">{a.memberTypes.ordinary}</SelectItem>
                                    <SelectItem value="extraordinary">{a.memberTypes.extraordinary}</SelectItem>
                                    <SelectItem value="supporting">{a.memberTypes.supporting}</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-2">
                            <Label className="font-sans text-sm">{a.motivation}</Label>
                            <Textarea data-testid="membership-motivation" rows={4} value={form.motivation} onChange={onChange("motivation")} className="rounded-xl bg-caireen-cream/50 border-caireen-ink/15" />
                        </div>
                        <Button
                            type="submit"
                            disabled={loading}
                            data-testid="membership-submit"
                            className="w-full rounded-full bg-caireen-orange hover:bg-caireen-red text-white font-semibold py-6 text-base"
                        >
                            {a.submitJoin}
                        </Button>
                    </motion.form>
                </section>
            </div>
        </div>
    );
}
